import React, { useState } from 'react';
import {
    List,
    Datagrid,
    TextField,
    EmailField,
    TextInput,
    FilterButton,
    CreateButton,
    ExportButton,
    TopToolbar,
    Show,
    SimpleShowLayout,
    DateField,
    ChipField,
    useRecordContext,
    useInput,
    Create,
    SimpleForm,
    Edit,
    SelectInput,
    BooleanField,
    BooleanInput,
    ReferenceInput,
    AutocompleteInput,
    required,
    ReferenceField,
    DeleteButton,
    EditButton,
    ShowButton,
    Labeled
} from 'react-admin';
import { Chip, Box, Typography, Tabs, Tab, Paper } from '@mui/material';
import { CustomerAvatar } from "../Users/CustomerAvatar";
import ReactMarkdown from 'react-markdown';
import MDEditor from '@uiw/react-md-editor';

const MessagesFilters = [
    <TextInput source="q" label="Buscar" alwaysOn />,
    <TextInput source="title" label="Título" />,
    <SelectInput source="message_type" label="Tipo" choices={[
        { id: 'general', name: 'General' },
        { id: 'personal', name: 'Personal' }
    ]} />,
    <BooleanInput source="is_active" label="Activo" />,
];

const MessageReadsFilters = [
    <TextInput source="q" label="Buscar" alwaysOn />,
    <ReferenceInput source="message_id" reference="messages">
        <AutocompleteInput optionText="title" label="Mensaje" />
    </ReferenceInput>,
    <ReferenceInput source="user_id" reference="users">
        <AutocompleteInput optionText="email" label="Usuario" />
    </ReferenceInput>,
];

const MessagesListActions = () => (
    <TopToolbar>
        <FilterButton />
        <CreateButton />
        <ExportButton />
    </TopToolbar>
);

const MessageReadsListActions = () => (
    <TopToolbar>
        <FilterButton />
        <ExportButton />
    </TopToolbar>
);

const MessageTypeField = ({ source }) => {
    const record = useRecordContext();
    if (!record) return null;
    const messageType = record[source];
    const color = messageType === 'general' ? 'primary' : 'secondary';
    const label = messageType === 'general' ? 'General' : 'Personal';
    return <Chip label={label} color={color} size="small" variant="outlined" />;
};

const ActiveStatusField = ({ source }) => {
    const record = useRecordContext();
    if (!record) return null;
    const isActive = record[source];
    const color = isActive ? 'success' : 'error';
    const label = isActive ? 'Activo' : 'Inactivo';
    return <Chip label={label} color={color} size="small" variant="filled" />;
};

const TargetUserField = ({ source }) => {
    const record = useRecordContext();
    if (!record || !record[source]) {
        return <span style={{ fontStyle: 'italic', color: '#666' }}>General</span>;
    }
    return <EmailField source="target_user_email" />;
};

const CreatedByField = ({ source }) => {
    const record = useRecordContext();
    if (!record || !record[source]) return <span>-</span>;
    return <EmailField source="created_by_email" />;
};

const MarkdownInput = ({ source, label, validate, ...rest }) => {
    const { field, fieldState } = useInput({ source, validate, ...rest });
    return (
        <Labeled label={label} fullWidth>
            <Box data-color-mode="light" sx={{ width: '100%' }}>
                <MDEditor
                    value={field.value || ''}
                    onChange={field.onChange}
                    height={300}
                    preview="edit"
                />
                {fieldState.error && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {fieldState.error.message}
                    </Typography>
                )}
            </Box>
        </Labeled>
    );
};

const MarkdownField = ({ source, label }) => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <Labeled label={label} fullWidth>
            <Box
                data-color-mode="light"
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 1, mb: 0.5 },
                    '& p': { mt: 0, mb: 1 },
                    '& ul, & ol': { pl: 3 },
                    '& code': { backgroundColor: '#f5f5f5', px: 0.5, borderRadius: 0.5 },
                    '& pre': { backgroundColor: '#f5f5f5', p: 1, borderRadius: 1, overflow: 'auto' },
                }}
            >
                <ReactMarkdown>{record[source] || ''}</ReactMarkdown>
            </Box>
        </Labeled>
    );
};

const MarkdownPreviewField = ({ source }) => {
    const record = useRecordContext();
    if (!record || !record[source]) return null;
    const stripped = record[source].replace(/[#*_`~[\]()>!]/g, '').slice(0, 120);
    return (
        <span style={{ maxWidth: 200, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#555' }}>
            {stripped}{record[source].length > 120 ? '…' : ''}
        </span>
    );
};

export const MessagesList = () => (
    <List
        filters={MessagesFilters}
        actions={<MessagesListActions />}
        perPage={25}
        sort={{ field: 'created_at', order: 'DESC' }}
    >
        <Datagrid>
            <TextField source="id" />
            <TextField source="title" label="Título" />
            <MarkdownPreviewField source="content" label="Contenido" />
            <MessageTypeField source="message_type" label="Tipo" />
            <TargetUserField source="target_user_id" label="Usuario Objetivo" />
            <CreatedByField source="created_by" label="Creado por" />
            <ActiveStatusField source="is_active" label="Estado" />
            <DateField source="created_at" label="Creado" showTime />
            <DateField source="updated_at" label="Actualizado" showTime />
            <ShowButton />
            <EditButton />
            <DeleteButton />
        </Datagrid>
    </List>
);

export const MessagesShow = () => (
    <Show>
        <SimpleShowLayout>
            <TextField source="id" />
            <TextField source="title" label="Título" />
            <MarkdownField source="content" label="Contenido" />
            <MessageTypeField source="message_type" label="Tipo" />
            <TargetUserField source="target_user_id" label="Usuario Objetivo" />
            <CreatedByField source="created_by" label="Creado por" />
            <ActiveStatusField source="is_active" label="Estado" />
            <DateField source="created_at" label="Creado" showTime />
            <DateField source="updated_at" label="Actualizado" showTime />
        </SimpleShowLayout>
    </Show>
);

export const MessagesCreate = () => (
    <Create>
        <SimpleForm>
            <TextInput source="title" label="Título" validate={[required()]} fullWidth />
            <MarkdownInput source="content" label="Contenido" validate={[required()]} fullWidth />
            <SelectInput
                source="message_type"
                label="Tipo de Mensaje"
                choices={[
                    { id: 'general', name: 'General (Todos los usuarios)' },
                    { id: 'personal', name: 'Personal (Usuario específico)' }
                ]}
                defaultValue="general"
                validate={[required()]}
            />
            <ReferenceInput
                source="target_user_id"
                reference="users"
                label="Usuario Objetivo (Solo para mensajes personales)"
                allowEmpty
            >
                <AutocompleteInput
                    optionText={(record) => `${record.email} (${record.username || 'Sin nombre'})`}
                    helperText="Dejar vacío para mensajes generales"
                />
            </ReferenceInput>
            <BooleanInput source="is_active" label="Activo" defaultValue={true} />
        </SimpleForm>
    </Create>
);

const DebugRecord = () => {
    const record = useRecordContext();
    console.log('Record data in edit:', record);
    return null;
};

export const MessagesEdit = () => (
    <Edit
        mutationMode="pessimistic"
        transform={(data) => {
            console.log('Transform data before submit:', data);
            if (data.target_user_id === '' || data.target_user_id === undefined) {
                data.target_user_id = null;
            }
            return data;
        }}
    >
        <SimpleForm>
            <DebugRecord />
            <TextInput source="title" label="Título" validate={[required()]} fullWidth />
            <MarkdownInput source="content" label="Contenido" validate={[required()]} fullWidth />
            <SelectInput
                source="message_type"
                label="Tipo de Mensaje"
                choices={[
                    { id: 'general', name: 'General (Todos los usuarios)' },
                    { id: 'personal', name: 'Personal (Usuario específico)' }
                ]}
                validate={[required()]}
            />
            <ReferenceInput
                source="target_user_id"
                reference="users"
                label="Usuario Objetivo (Solo para mensajes personales)"
                allowEmpty
            >
                <AutocompleteInput
                    optionText={(record) => `${record.email} (${record.username || 'Sin nombre'})`}
                    helperText="Dejar vacío para mensajes generales"
                />
            </ReferenceInput>
            <BooleanInput source="is_active" label="Activo" />
        </SimpleForm>
    </Edit>
);

export const MessageReadsList = () => (
    <List
        resource="messages_reads"
        filters={MessageReadsFilters}
        actions={<MessageReadsListActions />}
        perPage={25}
        sort={{ field: 'read_at', order: 'DESC' }}
    >
        <Datagrid>
            <TextField source="id" />
            <TextField source="title" />
            <TextField source="target_user_username" label="Read by" />
            <DateField source="first_read_at" label="Read on" showTime />
            <ShowButton />
        </Datagrid>
    </List>
);

export const MessageReadsShow = () => (
    <Show resource="messages_reads">
        <SimpleShowLayout>
            <TextField source="id" />
            <TextField source="title" />
            <MarkdownField source="content" label="Content" />
            <TextField source="target_user_username" label="Read by" />
            <DateField source="first_read_at" label="Read on" showTime />
        </SimpleShowLayout>
    </Show>
);

const MessagesManager = () => {
    const [tabValue, setTabValue] = useState(0);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const TabPanel = ({ children, value, index, ...other }) => {
        return (
            <div
                role="tabpanel"
                hidden={value !== index}
                id={`messages-tabpanel-${index}`}
                aria-labelledby={`messages-tab-${index}`}
                {...other}
            >
                {value === index && (
                    <Box sx={{ p: 3 }}>
                        {children}
                    </Box>
                )}
            </div>
        );
    };

    return (
        <Paper elevation={1} sx={{ m: 2 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h4" gutterBottom sx={{ p: 2, pb: 0 }}>
                    Administrador de Mensajes
                </Typography>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    sx={{ px: 2 }}
                    aria-label="tabs de mensajes"
                >
                    <Tab label="Administrar Mensajes" id="messages-tab-0" aria-controls="messages-tabpanel-0" />
                    <Tab label="Seguimiento de Lecturas" id="messages-tab-1" aria-controls="messages-tabpanel-1" />
                </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
                <Box>
                    <Typography variant="h6" gutterBottom>Administrar Mensajes</Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 2 }}>
                        Crear, editar y eliminar mensajes generales o personales para los usuarios
                    </Typography>
                    <MessagesList />
                </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
                <Box>
                    <Typography variant="h6" gutterBottom>Seguimiento de Lecturas de Mensajes</Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 2 }}>
                        Ver qué usuarios han leído cada mensaje y cuándo
                    </Typography>
                    <MessageReadsList />
                </Box>
            </TabPanel>
        </Paper>
    );
};

export default MessagesManager;